import { useState } from "react";
import { ApiError } from "@ferrivia/api-contract";
import type { Tracker } from "@ferrivia/analytics/browser";
import { useTranslation } from "@ferrivia/i18n";
import { useApi } from "./AuthProvider";
import type { SessionCredentials } from "./useSession";

export interface UseEmailOtpSignInOptions {
  onSignedIn: (credentials: SessionCredentials) => void;
  track?: Tracker;
}

export type EmailOtpStep = "email" | "code";

export interface UseEmailOtpSignInResult {
  step: EmailOtpStep;
  email: string;
  pending: boolean;
  error: string | null;
  requestCode: (email: string) => Promise<void>;
  submitCode: (code: string) => Promise<void>;
  resend: () => Promise<void>;
  changeEmail: () => void;
}

export function useEmailOtpSignIn({ onSignedIn, track }: UseEmailOtpSignInOptions): UseEmailOtpSignInResult {
  const api = useApi();
  const { t } = useTranslation("auth");
  const [step, setStep] = useState<EmailOtpStep>("email");
  const [email, setEmail] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function messageFor(code: string): string {
    switch (code) {
      case "invalid_code":
        return t("otp.error.invalid_code", "Koodi on virheellinen tai vanhentunut.");
      case "rate_limited":
        return t("otp.error.rate_limited", "Liian monta yritystä. Yritä hetken kuluttua uudelleen.");
      case "invalid_body":
        return t("otp.error.invalid_email", "Tarkista sähköpostiosoite.");
      case "forbidden":
        return t("otp.error.forbidden", "Tämä osoite kirjautuu sisään toisella tavalla.");
      default:
        return t("otp.error.fallback", "Jokin meni pieleen. Yritä uudelleen.");
    }
  }

  async function sendCode(address: string, event: "signin.otp_requested" | "signin.otp_resent") {
    setPending(true);
    setError(null);
    track?.track(event, {});
    try {
      await api.emailOtp.start({ email: address });
      setEmail(address);
      setStep("code");
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "";
      track?.track("signin.otp_failed", { code: code || "unknown" });
      setError(messageFor(code));
    } finally {
      setPending(false);
    }
  }

  async function requestCode(address: string) {
    await sendCode(address.trim(), "signin.otp_requested");
  }

  async function resend() {
    await sendCode(email, "signin.otp_resent");
  }

  async function submitCode(code: string) {
    setPending(true);
    setError(null);
    track?.track("signin.otp_code_submitted", {});
    try {
      const session = await api.emailOtp.verify({ email, code: code.trim() });
      onSignedIn({ token: session.token, expiresAt: session.expiresAt });
    } catch (err) {
      const errorCode = err instanceof ApiError ? err.code : "";
      track?.track("signin.otp_failed", { code: errorCode || "unknown" });
      setError(messageFor(errorCode));
      setPending(false);
    }
  }

  function changeEmail() {
    setStep("email");
    setError(null);
  }

  return { step, email, pending, error, requestCode, submitCode, resend, changeEmail };
}
